import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SearchInput from "src/components/widgets/SearchInput";
import NewCustomerModal from "src/components/modals/NewCustomerModal";
import BulkCustomerModal from "src/components/modals/BulkCustomerModal";
import CustomDataTable from "src/components/CustomDataTable";
import CustomModal from "src/components/CustomModal";
import { useQuery } from "@tanstack/react-query";
import { getCustomers } from "src/api/customer";
import CustomDropdown from "src/components/CustomDropdown";
import { cn, getCustomizationValue } from "src/lib/utils";
import { Button, buttonVariants } from "src/components/ui/button";
import { EllipsisIcon } from "src/svg";
import { useAuth } from "src/providers/AuthProvider";
import AddSubAccount from "src/components/modals/AddSubAccount";

const columns = (user, navigate) => [
	{ name: "Cust ID", selector: (row) => row.customer_id, grow: 0.75 },
	{ name: "First Name", selector: (row) => row.first_name },
	{ name: "Last Name", selector: (row) => row.last_name },
	{ name: "Email", selector: (row) => row.email, grow: 1.25 },
	{ name: "Phone", selector: (row) => row.phone, grow: 0.75 },
	{
		name: "Actions",
		cell: (row) => (
			<CustomDropdown
				trigger={
					<Button variant="hover">
						<EllipsisIcon />
					</Button>
				}
				actions={[
					{
						label: "View",
						onClick: () => navigate(`/customers/${row.uuid}`),
					},
					user?.organization_type_name === "Schools" && {
						label: (
							<CustomModal
								title={cn("Add ", getCustomizationValue(user, "sub_account_label", true))}
								trigger={<span>Add {getCustomizationValue(user, "sub_account_label", true)}</span>}
							>
								<AddSubAccount customer_id={row?.id} />
							</CustomModal>
						),
						asChild: true,
					},
				].filter(Boolean)}
			/>
		),
		grow: 0.5,
	},
];

const Customers = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const search = searchParams.get("search");
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const { data, isLoading } = useQuery({
		queryKey: ["customers", page, limit, search],
		queryFn: () => getCustomers({ page, limit, search }),
	});

	const customers = data?.data?.data ?? [];
	const totalRow = data?.data?.total;

	return (
		<>
			<div className="pageTop flex-cont flex-space-between flex-align-center">
				<h2>{getCustomizationValue(user, "customer_label")}</h2>
			</div>
			<div className="innerContent">
				<ActionBar />

				<CustomDataTable columns={columns(user, navigate)} data={customers} progressPending={isLoading} paginationTotalRows={totalRow} />
			</div>
		</>
	);
};

const ActionBar = () => {
	const { user } = useAuth();

	return (
		<div className="inner-top tw-flex-wrap tw-gap-3">
			<div className="inner-top-right tw-ml-auto tw-flex-wrap tw-gap-3">
				<SearchInput name="search" placeholder="Search..." />
				<CustomDropdown
					trigger={<button className="btn green-btn dropdown-btn">Add New {getCustomizationValue(user, "customer_label")}</button>}
					className="!tw-w-fit"
					actions={[
						{
							label: (
								<CustomModal
									title={cn("Add Single ", getCustomizationValue(user, "customer_label", true))}
									trigger={<button className="green-btn">Add Single {getCustomizationValue(user, "customer_label", true)}</button>}
								>
									<NewCustomerModal />
								</CustomModal>
							),
							asChild: true,
						},
						{
							label: (
								<CustomModal
									title={cn("Add Bulk ", getCustomizationValue(user, "customer_label"))}
									trigger={<button className="blue-btn">Bulk Upload {getCustomizationValue(user, "customer_label")}</button>}
								>
									<BulkCustomerModal />
								</CustomModal>
							),
							asChild: true,
						},
					]}
				/>
				<Link to="/customers/archived" className={cn("orange-btn", buttonVariants({ className: "!tw-h-[45px]" }))}>
					Archived {getCustomizationValue(user, "customer_label")}
				</Link>
			</div>
		</div>
	);
};

export default Customers;
